import { Text } from "@mantine/core";
import { IStorageMark } from "./types";
import { ReactNode } from "react";
import { formatPrice, formatQuantity } from "@common/utils/string";
import { SLIDER_MARKS_DEFAULT } from "./constants";

export function getMark(
  val: number,
  marks: IStorageMark[] = SLIDER_MARKS_DEFAULT,
): IStorageMark {
  const mark = marks.find(({ value }) => value === val);
  if (!mark) {
    throw new Error(`StorageSlider, getMark: mark is missing for "${val}"`);
  }

  return mark;
}

export function getMarkRealValue(
  val: number,
  marks: IStorageMark[] = SLIDER_MARKS_DEFAULT,
): number {
  const mark = marks.find(({ value }) => value === val);
  if (!mark) {
    throw new Error(
      `StorageSlider, getRealValue: mark is missing for "${val}"`,
    );
  }

  return mark.realValue;
}

export function getMarkValue(
  realVal: number,
  marks: IStorageMark[] = SLIDER_MARKS_DEFAULT,
): number {
  const mark = marks.find(({ realValue }) => realValue === realVal);
  if (!mark) {
    throw new Error(
      `StorageSlider, getValue: mark is missing for "${realVal}"`,
    );
  }

  return mark.value;
}

export function getLabel(
  { realValue, disabled }: IStorageMark,
  price: number,
  billingFrequency: string,
): ReactNode {
  return (
    <Text ta="center">
      <Text span fw={700} fz="sm">
        {formatQuantity(realValue)} rows
      </Text>
      <br />
      <Text span>
        {disabled ? "$0" : formatPrice(realValue * price)}/{billingFrequency}
      </Text>
    </Text>
  );
}

export function getNormalizedStorageValue(
  value: string[] | string | number | undefined,
  marks: IStorageMark[] = SLIDER_MARKS_DEFAULT,
): number | undefined {
  // string[]
  if (Array.isArray(value)) {
    return getNormalizedStorageValue(value[0], marks);
  }
  // string
  else if (typeof value === "string") {
    const valueAsNumber = parseInt(String(value));
    if (!isNaN(valueAsNumber)) {
      return getNormalizedStorageValue(valueAsNumber, marks);
    }
  }
  // number
  else if (typeof value === "number" && value > 0) {
    // smaller than first mark
    const [firstMark] = marks;
    if (value <= firstMark.realValue) {
      return firstMark.realValue;
    }

    // bigger than last mark
    const lastMark = marks[marks.length - 1];
    if (value >= lastMark.realValue) {
      return lastMark.realValue;
    }

    // find closest mark
    const [closestMark] = [...marks].sort(
      (mark1, mark2) =>
        Math.abs(mark1.realValue - value) - Math.abs(mark2.realValue - value),
    );
    return closestMark.realValue;
  }

  return undefined;
}
